import { Suspense, lazy } from "react";
import heroVideo from "@/assets/hero-video-roof-aerial.mp4";
import heroFallback from "@/assets/hero-fallback-aerial.jpg";

const HeroForm = lazy(() => import("./HeroForm").then(m => ({ default: m.HeroForm })));

const FormPlaceholder = () => (
  <div className="bg-card/95 backdrop-blur-xs rounded-2xl p-6 shadow-2xl border border-border/50 w-full max-w-md animate-pulse">
    <div className="text-center mb-5">
      <div className="h-6 bg-muted rounded-sm w-2/3 mx-auto mb-2" />
      <div className="h-4 bg-muted rounded-sm w-1/2 mx-auto" />
    </div>
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="h-10 bg-muted rounded-sm" />
        <div className="h-10 bg-muted rounded-sm" />
      </div>
      <div className="h-10 bg-muted rounded-sm" />
      <div className="h-12 bg-secondary/50 rounded-sm" />
    </div>
  </div>
);

export const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center pt-44 lg:pt-36" aria-labelledby="hero-heading">
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          poster={heroFallback}
          className="w-full h-full object-cover"
          aria-hidden="true"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 gradient-overlay" />
      </div>

      <div className="relative container mx-auto px-4 py-12 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="text-center lg:text-left">
            <h1 id="hero-heading" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-primary-foreground mb-4 lg:mb-6 leading-tight">
              Minnesota's Trusted Roofing & Siding Experts
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-primary-foreground/90 mb-6 lg:mb-8 leading-relaxed">
              Quality roof replacements, siding, decks, and gutters—protecting Minnesota homes with craftsmanship you can trust.
            </p>
            <div className="flex items-center justify-center lg:justify-start gap-4 sm:gap-6 text-primary-foreground/80" role="list">
              <div className="flex items-center gap-2" role="listitem">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground font-bold text-sm sm:text-base">
                  ✓
                </div>
                <span className="text-xs sm:text-sm">Licensed &<br/>Insured</span>
              </div>
              <div className="flex items-center gap-2" role="listitem">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground font-bold text-xs sm:text-sm">
                  ★
                </div>
                <span className="text-xs sm:text-sm">5-Star<br/>Reviews</span>
              </div>
              <div className="flex items-center gap-2" role="listitem">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground font-bold text-xs sm:text-sm">
                  MN
                </div>
                <span className="text-xs sm:text-sm">Local<br/>Experts</span>
              </div>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <Suspense fallback={<FormPlaceholder />}>
              <HeroForm />
            </Suspense>
          </div>
        </div>
      </div>
    </section>
  );
};
